import type { LucideIcon } from 'lucide-react';
import * as Icons from 'lucide-react';

/**
 * Dynamically loads an icon component from lucide-react by name
 * @param iconName - The name of the icon to load
 * @returns The icon component or null if not found
 */
export function getIcon(iconName: string | LucideIcon): LucideIcon | null {
  if (!iconName) return null;

  // If it's already an icon component, return it
  if (typeof iconName === 'function') {
    return iconName;
  }

  // Handle different naming conventions
  const normalizedName = iconName.charAt(0).toUpperCase() + iconName.slice(1);

  return (Icons[normalizedName as keyof typeof Icons] as LucideIcon) || null;
}

/**
 * Maps color names to Tailwind CSS classes
 * @param color - Color name or Tailwind class
 * @returns Tailwind color class
 */
export function getColorClass(color?: string): string {
  if (!color) return 'text-icon-default';

  // If it's already a Tailwind class, return it
  if (color.startsWith('text-') || color.startsWith('bg-')) {
    return color;
  }

  const colorMap: Record<string, string> = {
    // Basic colors
    blue: 'text-icon-blue',
    purple: 'text-icon-purple',
    green: 'text-icon-green',
    gray: 'text-icon-gray',
    red: 'text-icon-red',
    yellow: 'text-icon-yellow',
    indigo: 'text-icon-indigo',
    pink: 'text-icon-pink',

    // Semantic colors
    info: 'text-icon-info',
    success: 'text-icon-success',
    warning: 'text-icon-warning',
    error: 'text-icon-error',
    default: 'text-icon-default',

    // Brand colors
    primary: 'text-icon-primary',
    secondary: 'text-icon-secondary',
    accent: 'text-icon-accent',
  };

  return colorMap[color] || 'text-icon-default';
}

/**
 * Maps background color names to Tailwind CSS classes
 * @param color - Color name or Tailwind class
 * @returns Tailwind background color class
 */
export function getBackgroundColorClass(color?: string): string {
  if (!color) return 'bg-gray-100';

  // If it's already a Tailwind class, return it
  if (color.startsWith('bg-')) {
    return color;
  }

  const colorMap: Record<string, string> = {
    // Basic colors
    blue: 'bg-blue-100',
    purple: 'bg-purple-100',
    green: 'bg-green-100',
    gray: 'bg-gray-100',
    red: 'bg-red-100',
    yellow: 'bg-yellow-100',
    indigo: 'bg-indigo-100',
    pink: 'bg-pink-100',

    // Semantic colors
    info: 'bg-blue-100',
    success: 'bg-green-100',
    warning: 'bg-yellow-100',
    error: 'bg-red-100',
    default: 'bg-gray-100',

    // Brand colors with opacity
    primary: 'bg-primary/10',
    secondary: 'bg-secondary/10',
    accent: 'bg-accent/10',
  };

  return colorMap[color] || 'bg-gray-100';
}

/**
 * Formats a number with appropriate separators
 * @param value - The value to format
 * @param options - Intl.NumberFormat options
 * @returns Formatted number string
 */
export function formatNumber(
  value: number | string,
  options?: Intl.NumberFormatOptions
): string {
  const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;

  if (isNaN(num)) return value.toString();

  return new Intl.NumberFormat('en-US', {
    ...options,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).format(num);
}

/**
 * Extracts numeric value from a string with prefix/suffix
 * @param value - The value string (e.g., "$1.5M", "30-50%")
 * @returns Object with value, prefix, and suffix
 */
export function parseMetricValue(value: string): {
  value: string;
  prefix?: string;
  suffix?: string;
} {
  // Handle range values (e.g., "30-50%")
  if (value.includes('-') && /\d-\d/.test(value)) {
    const match = value.match(/^([^0-9-]*)(.+?)([^0-9-]*)$/);
    if (match) {
      return {
        prefix: match[1] || undefined,
        value: match[2],
        suffix: match[3] || undefined,
      };
    }
  }

  // Handle single values
  const match = value.match(/^([^0-9.]*)([0-9.,]+)(.*)$/);
  if (match) {
    return {
      prefix: match[1] || undefined,
      value: match[2],
      suffix: match[3] || undefined,
    };
  }

  return { value };
}
